import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  AvatarGroup,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  SportsEsports as GameIcon
} from '@mui/icons-material';
import TournamentBracket from '../components/TournamentBracket';
import tournamentService from '../services/tournamentService';

const TournamentBracketPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        setLoading(true);
        // First try to load from API
        const data = await tournamentService.getTournamentById(id);
        setTournament(data);
      } catch (apiError) {
        console.error('Error fetching tournament details from API:', apiError);
        
        // Try to retrieve tournament data from localStorage as fallback
        try {
          const matches = JSON.parse(localStorage.getItem('tournament_matches'));
          const type = localStorage.getItem('tournament_type');
          
          if (matches && type) {
            // Create minimal tournament object with just the data we need
            setTournament({
              id,
              matches,
              type,
              name: `Torneo ${id}`,
              participants: []
            });
          } else {
            setError('No se encontraron datos del torneo');
          }
        } catch (localStorageError) {
          console.error('Error retrieving data from localStorage:', localStorageError);
          setError('Error al cargar los datos del torneo');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [id]);

  const handleBack = () => {
    navigate(`/tournaments/${id}`);
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'single_elimination': return 'Eliminación Simple';
      case 'double_elimination': return 'Doble Eliminación';
      case 'round_robin': return 'Todos contra Todos';
      default: return type;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '80vh',
        gap: 2
      }}>
        <Alert severity="error">{error || 'No se encontraron datos del torneo'}</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate(`/tournaments/${id}`)}
          startIcon={<ArrowBackIcon />}
        >
          Volver al Torneo
        </Button>
      </Box>
    );
  }

  if (!tournament.matches || tournament.matches.length === 0) {
    return (
      <Box sx={{ 
        p: { xs: 1, sm: 1 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '90%',
        maxWidth: '1000px',
        mx: 'auto',
        gap: 2
      }}>
        <Paper
          elevation={0}
          sx={{ 
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton 
              onClick={handleBack}
              sx={{ 
                width: 36,
                height: 36,
                bgcolor: 'action.hover',
                color: 'text.primary',
                '&:hover': { 
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Bracket del Torneo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tournament.name}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ width: '100%' }}>
          El torneo aún no tiene partidas programadas.
        </Alert>

        <Button 
          variant="contained" 
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: 1.5,
            textTransform: 'none',
            bgcolor: '#c89e38',
            '&:hover': {
              bgcolor: '#b38d2d'
            }
          }}
        >
          Volver al Torneo
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '1000px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{ 
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton 
                onClick={handleBack}
                sx={{ 
                  width: 36,
                  height: 36,
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Bracket del Torneo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tournament.name}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                icon={<GameIcon />}
                label={getTypeLabel(tournament.type)}
                size="small"
                sx={{ 
                  borderRadius: 1,
                  bgcolor: 'rgba(200, 158, 56, 0.1)',
                  color: '#c89e38',
                  border: '1px solid rgba(200, 158, 56, 0.3)'
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Tournament Bracket */}
        <TournamentBracket 
          matches={tournament.matches}
          type={tournament.type}
        />

        {/* Back Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              borderColor: '#c89e38',
              color: '#c89e38',
              '&:hover': {
                borderColor: '#b38d2d',
                bgcolor: 'rgba(200, 158, 56, 0.04)'
              }
            }}
          >
            Volver al Torneo
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TournamentBracketPage; 